import React, { Component } from "react";
import { observer, inject } from "mobx-react";
import {
  TextArea,
  Button,
  FormGroup,
  InputGroup,
  Intent,
  Icon,
  Switch,
  Drawer,
  Position
} from "@blueprintjs/core";

import Inputmask from "inputmask";
import WorkOrdersTable from "components/Client/WorkOrdersTable";
import QuotesTable from "components/Client/QuotesTable";
import InvoicesTable from "components/Client/InvoicesTable";
import ProjectsTable from "components/Client/ProjectsTable";
import LocationsTable from "components/Client/LocationsTable";
import ServiceAgreementsTable from "components/Client/ServiceAgreementsTable";
import EquipmentsTable from "components/Client/EquipmentsTable";
import ClientJobsTable from "components/Client/JobsTable";
import MoveEquipmentPanel from "components/Equipment/MoveEquipmentPanel";
import AddEstimateModal from "components/Quote/AddEstimateModal";
import AddInvoiceModal from "components/Invoice/AddInvoiceModal";
import AddWorkReportModal from "components/WorkReport/AddWorkReportModal";
import AddProjectModal from "components/Project/AddProjectModal";
import AddEquipmentModal from "components/Equipment/AddEquipmentModal";
import AddLocationModal from "components/Client/AddLocationModal";
import Toast from "helpers/toaster";
import * as DataList from "components/DataList";

// Client page (styles/client-page.scss): a header with the client's name,
// contact links and flags, and "Edit details" opening the form in a panel from
// the right; below, one strip that is both the record counts and the tabs,
// then the selected records (components/Client/*Table).

const initials = (name = "") =>
  name
    .replace(/[^A-Za-z0-9 ]/g, " ")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("") || "?";

// "Work Orders" -> "Work orders" (the API names these in title case).
const sentenceCase = (text = "") =>
  text ? text.charAt(0) + text.slice(1).toLowerCase() : "";

// What each tab's add button says and opens. Jobs and service agreements open
// the side panel (App.js JobSidebar); the rest open their "New" dialog. All
// start with this client chosen.
const ADD_ACTIONS = {
  jobs: { label: "New job", open: "job" },
  work_orders: { label: "New work order", open: "work_order" },
  equipments: { label: "Add equipment", open: "equipment" },
  estimates: { label: "New estimate", open: "estimate" },
  invoices: { label: "New invoice", open: "invoice" },
  locations: { label: "Add location", open: "location" },
  projects: { label: "New project", open: "project" },
  service_agreements: { label: "New service agreement", open: "service_agreement" }
};

const ADDITIONAL_EMAIL_TYPES = [
  "Purchase Order",
  "Accounts Payable",
  "Copy To",
  "Quote",
  "Other"
];
@inject("store", "clientStore", "equipmentStore", "viewStore", "jobStore", "serviceAgreementStore")
@observer
class ClientProfileCard extends Component {
  constructor(props) {
    super(props);

    const initialAdditionalEmails = this.buildInitialAdditionalEmails(
      props.client
    );

    this.state = {
      saving: false,
      // The header shows what's saved, not what's being typed in the panel.
      saved: this.summaryOf(props.client),
      isEditOpen: false,
      isMoveEquipmentPanelOpen: false,
      openDialog: null, // estimate | invoice | work_order | project | equipment | location
      tableKey: 0, // bumped to reload the selected tab's records
      additionalEmails: initialAdditionalEmails
    };
  }

  componentDidMount() {
    let { client } = this.props;
    this.props.clientStore.loadAnalytics({ uuid: client.attributes.id });
  }

  changeTableData = (resource) => {
    if (resource) {
      this.props.onResourceChange(resource);
    }
  };

  resourceData = () => {
    let { client, selectedResource } = this.props;
    if (selectedResource === "jobs") {
      return (
        <React.Fragment>
          {/* <TableHeading heading="JOBS" /> */}
          <ClientJobsTable client={client} />
        </React.Fragment>
      );
    } else if (selectedResource === "work_orders") {
      return (
        <React.Fragment>
          {/* <TableHeading heading="WORK ORDERS" /> */}
          <WorkOrdersTable client={client} />
        </React.Fragment>
      );
    } else if (selectedResource === "equipments") {
      return (
        <React.Fragment>
          {/* <TableHeading heading="EQUIPMENTS" /> */}
          <EquipmentsTable client={client} />
        </React.Fragment>
      );
    } else if (selectedResource === "estimates") {
      return (
        <React.Fragment>
          {/* <TableHeading heading="ESTIMATES" /> */}
          <QuotesTable client={client} />
        </React.Fragment>
      );
    } else if (selectedResource === "invoices") {
      return (
        <React.Fragment>
          <InvoicesTable client={client} />
        </React.Fragment>
      );
    } else if (selectedResource === "projects") {
      return (
        <React.Fragment>
          <ProjectsTable client={client} />
        </React.Fragment>
      );
    } else if (selectedResource === "locations") {
      return (
        <React.Fragment>
          <LocationsTable client={client} />
        </React.Fragment>
      );
    } else if (selectedResource === "service_agreements") {
      return (
        <React.Fragment>
          <ServiceAgreementsTable client={client} />
        </React.Fragment>
      );
    }
  };

  componentDidUpdate(prevProps) {
    if (
      prevProps.client?.attributes?.id !== this.props.client?.attributes?.id
    ) {
      const additionalEmails = this.buildInitialAdditionalEmails(
        this.props.client
      );
      this.setState({ additionalEmails });
      this.updateClientAdditionalEmails(additionalEmails);
    }
  }

  buildInitialAdditionalEmails(client) {
    const existing =
      client?.attributes?.additional_emails &&
      client.attributes.additional_emails.length
        ? client.attributes.additional_emails
        : null;

    if (existing) {
      return existing.map((item) => ({
        email: item.email || "",
        type: item.type || ADDITIONAL_EMAIL_TYPES[0]
      }));
    }

    return [
      {
        email: "",
        type: ADDITIONAL_EMAIL_TYPES[0]
      }
    ];
  }

  updateClientAdditionalEmails(additionalEmails) {
    const sanitizedAdditionalEmails = additionalEmails
      .filter((item) => item.email && item.email.trim() !== "")
      .map((item) => ({
        email: item.email.trim(),
        type: item.type || ADDITIONAL_EMAIL_TYPES[0]
      }));

    this.props.client.attributes.additional_emails = sanitizedAdditionalEmails;
  }

  handleAdditionalEmailChange = (index, key, value) => {
    this.setState((prevState) => {
      const additionalEmails = prevState.additionalEmails.map((item, idx) =>
        idx === index ? { ...item, [key]: value } : item
      );

      this.updateClientAdditionalEmails(additionalEmails);

      return { additionalEmails };
    });
  };

  addAdditionalEmail = () => {
    this.setState((prevState) => {
      const additionalEmails = [
        ...prevState.additionalEmails,
        { email: "", type: ADDITIONAL_EMAIL_TYPES[0] }
      ];

      this.updateClientAdditionalEmails(additionalEmails);

      return { additionalEmails };
    });
  };

  removeAdditionalEmail = (index) => {
    this.setState((prevState) => {
      const additionalEmails = prevState.additionalEmails.filter(
        (_, idx) => idx !== index
      );

      const nextAdditionalEmails =
        additionalEmails.length > 0
          ? additionalEmails
          : [{ email: "", type: ADDITIONAL_EMAIL_TYPES[0] }];

      this.updateClientAdditionalEmails(nextAdditionalEmails);

      return { additionalEmails: nextAdditionalEmails };
    });
  };

  handleClientChange(event, key) {
    this.props.client.attributes[key] =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
  }

  updateClient() {
    if (this.props.client.attributes.name === "") {
      Toast.show({
        icon: "warning-sign",
        message: "Please enter valid name!",
        intent: Intent.DANGER
      });
      return;
    }

    if (this.props.client.attributes.legal_name === "") {
      Toast.show({
        icon: "warning-sign",
        message: "Please enter valid legal name!",
        intent: Intent.DANGER
      });
      return;
    }

    if (this.props.client.attributes.phone === "") {
      Toast.show({
        icon: "warning-sign",
        message: "Please enter phone!",
        intent: Intent.DANGER
      });
      return;
    }

    if (this.props.client.attributes.email === "") {
      Toast.show({
        icon: "warning-sign",
        message: "Please enter valid email!",
        intent: Intent.DANGER
      });
      return;
    }

    this.setState({ saving: true });
    this.props.clientStore
      .updateClient(this.props.client)
      .then(this.onSuccessfullUpdateClient.bind(this))
      .catch(this.onFailClientUpdate.bind(this))
      .then(() => this.setState({ saving: false }));
  }

  summaryOf(client) {
    const attributes = (client && client.attributes) || {};
    return {
      name: attributes.name,
      legal_name: attributes.legal_name,
      phone: attributes.phone,
      email: attributes.email,
      do_not_service: Boolean(attributes.do_not_service),
      do_not_service_reason: attributes.do_not_service_reason,
      is_on_alert: Boolean(attributes.is_on_alert),
      on_alert_reason: attributes.on_alert_reason
    };
  }

  // Turning a flag on reveals its reason box: bring it into view and focus it.
  toggleFlag(event, key, reasonId) {
    this.handleClientChange(event, key);
    if (event.target.checked) {
      setTimeout(() => {
        const box = document.getElementById(reasonId);
        if (box) {
          box.scrollIntoView({ block: "nearest", behavior: "smooth" });
          box.focus({ preventScroll: true });
        }
      }, 0);
    }
  }

  // Edits go straight into the client's attributes (as before); Cancel puts
  // back what was there when the panel opened.
  openEdit = () => {
    const attributes = this.props.client.attributes;
    this.editSnapshot = {
      ...attributes,
      additional_emails: (attributes.additional_emails || []).map((item) => ({ ...item }))
    };
    this.setState({
      isEditOpen: true,
      additionalEmails: this.buildInitialAdditionalEmails(this.props.client)
    });
  };

  cancelEdit = () => {
    if (this.state.saving) return;
    if (this.editSnapshot) {
      Object.assign(this.props.client.attributes, this.editSnapshot);
      this.editSnapshot = null;
    }
    this.setState({ isEditOpen: false });
  };

  // The phone and email masks need the fields, which exist once the panel is open.
  maskFields = () => {
    Inputmask({
      mask: "999-999-9999[999]",
      skipOptionalPartCharacter: " ",
      clearMaskOnLostFocus: true,
      greedy: false
    }).mask("#phone");
    Inputmask({ alias: "email" }).mask("#email");
    Inputmask({ alias: "email" }).mask("#email_primary");
  };

  onSuccessfullUpdateClient(response) {
    this.editSnapshot = null;
    this.setState({ saved: this.summaryOf(this.props.client), isEditOpen: false });
    Toast.show({
      icon: "tick",
      message: `Changes to ${this.props.client.attributes.name} saved.`,
      intent: Intent.SUCCESS
    });
  }

  onFailClientUpdate(error) {
    if (error.response && error.response.data && error.response.data.error) {
      Toast.show({
        icon: "warning-sign",
        message: error.response.data.error,
        intent: Intent.DANGER
      });
    } else {
      Toast.show({
        icon: "warning-sign",
        message: "Something went wrong. Failed to update client.",
        intent: Intent.DANGER
      });
    }
  }

  openAdd(kind) {
    const { client, store, jobStore, serviceAgreementStore } = this.props;
    if (kind === "job") {
      jobStore.clearJob();
      jobStore.isServiceAgreement = false;
      jobStore.persistJob.object.client_id = client.id;
      jobStore.persistJob.object.client = client;
      store.isOpen = true;
    } else if (kind === "service_agreement") {
      serviceAgreementStore.clearServiceAgreement();
      serviceAgreementStore.persistServiceAgreement.object.client_id = client.id;
      serviceAgreementStore.persistServiceAgreement.object.client = client;
      jobStore.isServiceAgreement = true;
      store.isOpen = true;
    } else {
      this.setState({ openDialog: kind });
    }
  }

  closeAdd = () => {
    this.setState({ openDialog: null });
  };

  // Something was added for this client: reload the selected tab and the counts.
  // (Equipment shows its own message; estimates, invoices and work orders open
  // the new record instead.)
  onAdded = () => {
    const { client, clientStore } = this.props;
    const message = { project: "Project created.", location: "Location added." }[this.state.openDialog];
    this.setState((state) => ({ openDialog: null, tableKey: state.tableKey + 1 }));
    clientStore.loadAnalytics({ uuid: client.attributes.id });
    if (message) Toast.show({ icon: "tick", message, intent: Intent.SUCCESS });
  };

  openMoveEquipmentPanel() {
    this.setState({ isMoveEquipmentPanelOpen: true });
  }

  closeMoveEquipmentPanel() {
    this.setState({ isMoveEquipmentPanelOpen: false });
  }

  onMoveEquipmentSuccess(response) {
    this.closeMoveEquipmentPanel();
    // Ensure the client equipments search is scoped to this client and reload
    try {
      if (this.props.viewStore && this.props.viewStore.clientEquipmentSearch) {
        this.props.viewStore.clientEquipmentSearch.where =
          this.props.viewStore.clientEquipmentSearch.where || {};
        this.props.viewStore.clientEquipmentSearch.where.client_id =
          this.props.client.attributes.id;
      }

      if (this.props.equipmentStore) {
        this.props.equipmentStore.loadClientEquipments();
      }
    } catch (e) {
      // swallow errors; we still want to close the modal
      console.error("Error refreshing equipments after move:", e);
    }

    // Also notify parent that resource should be switched to equipments (if the parent handles tabs)
    if (this.props.onResourceChange) {
      this.props.onResourceChange("equipments");
    }
  }

  render() {
    const {
      client,
      selectedResource,
      clientStore: {
        client: { isLoading },
        clientAnalytics
      }
    } = this.props;
    const attributes = client.attributes;
    const saved = this.state.saved;
    const resources = clientAnalytics.objects || [];

    return (
      <div className="cp">
        <header className="cp-head" aria-label="Client">
          <span className="cp-avatar" aria-hidden="true">{initials(saved.name)}</span>
          <div className="cp-head__who">
            <h1 className="cp-head__name">{saved.name}</h1>
            <div className="cp-head__meta">
              {saved.legal_name && saved.legal_name !== saved.name && (
                <span className="cp-head__legal">{saved.legal_name}</span>
              )}
              {saved.phone && (
                <a href={`tel:${saved.phone}`}><Icon icon="phone" iconSize={12} />{saved.phone}</a>
              )}
              {saved.email && (
                <a href={`mailto:${saved.email}`}><Icon icon="envelope" iconSize={12} />{saved.email}</a>
              )}
            </div>
          </div>
          <Button className="cp-head__edit" icon="edit" text="Edit details" onClick={this.openEdit} />
          {(saved.do_not_service || saved.is_on_alert) && (
            <div className="cp-head__flags">
              {saved.do_not_service && (
                <div className="cp-flag cp-flag--stop" role="note">
                  <Icon icon="disable" iconSize={14} />
                  <span><strong>Do not service this client.</strong>{saved.do_not_service_reason ? ` ${saved.do_not_service_reason}` : ''}</span>
                </div>
              )}
              {saved.is_on_alert && (
                <div className="cp-flag cp-flag--alert" role="note">
                  <Icon icon="warning-sign" iconSize={14} />
                  <span><strong>Alert when creating a job.</strong>{saved.on_alert_reason ? ` ${saved.on_alert_reason}` : ''}</span>
                </div>
              )}
            </div>
          )}
        </header>

        <Drawer
          className="cp-edit"
          title="Edit details"
          icon="edit"
          position={Position.LEFT}
          size="min(520px, 100vw)"
          isOpen={this.state.isEditOpen}
          onOpened={this.maskFields}
          onClose={this.cancelEdit}
          canOutsideClickClose={!this.state.saving}
          canEscapeKeyClose={!this.state.saving}
        >
          <div className="cp-edit__form">
            <section className="cp-group">
              <h2 className="cp-group__title">Contact</h2>
              <FormGroup label="Name" labelFor="client-name" labelInfo="*">
                <InputGroup
                  id="client-name"
                  placeholder="Client name"
                  defaultValue={attributes.name}
                  onChange={(event) => this.handleClientChange(event, "name")}
                />
              </FormGroup>
              <FormGroup label="Legal name" labelFor="client-legal-name" labelInfo="*">
                <InputGroup
                  id="client-legal-name"
                  placeholder="As it appears on invoices"
                  defaultValue={attributes.legal_name}
                  onChange={(event) => this.handleClientChange(event, "legal_name")}
                />
              </FormGroup>
              <FormGroup label="Email" labelFor="email_primary" labelInfo="*">
                <InputGroup
                  placeholder="name@example.com"
                  defaultValue={attributes.email}
                  id="email_primary"
                  onChange={(event) => this.handleClientChange(event, "email")}
                />
              </FormGroup>
              <FormGroup label="Phone" labelFor="phone" labelInfo="*">
                <InputGroup
                  placeholder="000-000-0000"
                  defaultValue={attributes.phone}
                  id="phone"
                  onChange={(event) => this.handleClientChange(event, "phone")}
                />
              </FormGroup>
              <FormGroup label="Fax" labelFor="client-fax">
                <InputGroup
                  id="client-fax"
                  placeholder="Fax number"
                  defaultValue={attributes.fax}
                  onChange={(event) => this.handleClientChange(event, "fax")}
                />
              </FormGroup>
              <div className="cp-emails">
                <span className="cp-label">More emails</span>
                <p className="cp-help">Who else gets purchase orders, invoices and estimates.</p>
                {this.state.additionalEmails.map((additionalEmail, index) => (
                  <div key={`additional-email-${index}`} className="cp-email">
                    <InputGroup
                      placeholder="name@example.com"
                      type="email"
                      aria-label={`Email ${index + 1}`}
                      id={index === 0 ? "email" : undefined}
                      value={additionalEmail.email}
                      onChange={(event) => this.handleAdditionalEmailChange(index, "email", event.target.value)}
                    />
                    <div className="cp-email__meta">
                      <label className="cp-email__receives" htmlFor={`client-email-type-${index}`}>Receives</label>
                      <div className="bp3-select cp-email__type">
                        <select
                          id={`client-email-type-${index}`}
                          value={additionalEmail.type}
                          onChange={(event) => this.handleAdditionalEmailChange(index, "type", event.target.value)}
                        >
                          {ADDITIONAL_EMAIL_TYPES.map((type) => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                      {index > 0 && (
                        <Button
                          minimal
                          small
                          icon="cross"
                          className="cp-email__remove"
                          aria-label={`Remove email ${index + 1}`}
                          onClick={() => this.removeAdditionalEmail(index)}
                        />
                      )}
                    </div>
                  </div>
                ))}
                <Button minimal small icon="add" className="cp-link-button" text="Add another email" onClick={this.addAdditionalEmail} />
              </div>
            </section>

            <section className="cp-group">
              <h2 className="cp-group__title">Billing</h2>
              <div className="cp-grid">
                <FormGroup label="Terms" labelFor="client-terms">
                  <div className="bp3-select bp3-fill">
                    <select
                      id="client-terms"
                      defaultValue={attributes.terms}
                      onChange={(event) => this.handleClientChange(event, "terms")}
                    >
                      <option value="">No terms</option>
                      <option value="Net 15">Net 15</option>
                      <option value="Net 30">Net 30</option>
                      <option value="Net 45">Net 45</option>
                      <option value="Net 60">Net 60</option>
                      <option value="Net 60">Net 90</option>
                      <option value="Upon Completion">Upon completion</option>
                    </select>
                  </div>
                </FormGroup>
                <FormGroup label="Invoices due" labelFor="client-due-days">
                  <div className="bp3-select bp3-fill">
                    <select
                      id="client-due-days"
                      defaultValue={attributes.invoice_due_within_days}
                      onChange={(event) => this.handleClientChange(event, "invoice_due_within_days")}
                    >
                      <option value="">Default</option>
                      {[7, 15, 30, 45, 60, 75, 90, 105, 120].map((days) => (
                        <option key={days} value={String(days)}>{`In ${days} days`}</option>
                      ))}
                    </select>
                  </div>
                </FormGroup>
              </div>
              <FormGroup label="Invoice to" labelFor="client-invoice-to">
                <TextArea
                  id="client-invoice-to"
                  rows={3}
                  fill={true}
                  placeholder="Billing name and address"
                  defaultValue={attributes.invoice_to}
                  onChange={(event) => this.handleClientChange(event, "invoice_to")}
                />
              </FormGroup>
              <FormGroup label="Ship to" labelFor="client-ship-to">
                <TextArea
                  id="client-ship-to"
                  rows={3}
                  fill={true}
                  placeholder="Shipping name and address"
                  defaultValue={attributes.ship_to}
                  onChange={(event) => this.handleClientChange(event, "ship_to")}
                />
              </FormGroup>
            </section>

            <section className="cp-group">
              <h2 className="cp-group__title">Notes</h2>
              <FormGroup label="Description" labelFor="client-description">
                <TextArea
                  id="client-description"
                  rows={4}
                  fill={true}
                  placeholder="Anything the team should know about this client"
                  defaultValue={attributes.description}
                  onChange={(event) => this.handleClientChange(event, "description")}
                />
              </FormGroup>
            </section>

            <section className="cp-group">
              <h2 className="cp-group__title">Service</h2>
              <Switch
                className="cp-switch"
                checked={Boolean(attributes.do_not_service)}
                label="Do not service this client"
                onChange={(event) => this.toggleFlag(event, "do_not_service", "client-do-not-service-reason")}
              />
              {attributes.do_not_service && (
                <TextArea
                  className="cp-reason"
                  rows={2}
                  fill={true}
                  id="client-do-not-service-reason"
                  aria-label="Why this client isn't serviced"
                  placeholder="Why, so the team knows"
                  defaultValue={attributes.do_not_service_reason}
                  onChange={(event) => this.handleClientChange(event, "do_not_service_reason")}
                />
              )}
              <Switch
                className="cp-switch"
                checked={Boolean(attributes.is_on_alert)}
                label="Show an alert when creating a job"
                onChange={(event) => this.toggleFlag(event, "is_on_alert", "client-alert-reason")}
              />
              {attributes.is_on_alert && (
                <TextArea
                  className="cp-reason"
                  rows={2}
                  fill={true}
                  id="client-alert-reason"
                  aria-label="Alert message"
                  placeholder="What the alert should say"
                  defaultValue={attributes.on_alert_reason}
                  onChange={(event) => this.handleClientChange(event, "on_alert_reason")}
                />
              )}
            </section>
          </div>

          <footer className="cp-edit__footer">
            <Button text="Cancel" onClick={this.cancelEdit} disabled={this.state.saving} />
            <Button
              intent={Intent.PRIMARY}
              icon="floppy-disk"
              text="Save changes"
              onClick={this.updateClient.bind(this)}
              loading={this.state.saving}
            />
          </footer>
        </Drawer>

        <section className="cp-main" aria-label="Client records">
          <div className="cp-tabs" role="tablist" aria-label="Records">
            {clientAnalytics.isLoading && resources.length === 0
              ? Array.from({ length: 8 }, (_, index) => (
                  <span key={index} className="cp-tab cp-tab--loading" aria-hidden="true">
                    <span className="dl-bone cp-bone--label" />
                    <span className="dl-bone cp-bone--count" />
                  </span>
                ))
              : resources.map((resource) => {
                  const active = resource.id === selectedResource;
                  const parts = (resource.breakdown || []).filter((part) => String(part.name).toLowerCase() !== "all");
                  return (
                    <button
                      key={resource.id}
                      type="button"
                      role="tab"
                      aria-selected={active ? "true" : "false"}
                      className={`cp-tab${active ? " is-active" : ""}`}
                      onClick={() => this.changeTableData(resource.id)}
                    >
                      <span className="cp-tab__label">{sentenceCase(resource.name)}</span>
                      <span className="cp-tab__count">{resource.total_count}</span>
                      {parts.length > 0 && (
                        <span className="cp-tab__parts">
                          {parts.map((part) => (
                            <span key={part.name} className={`cp-tab__part cp-tab__part--${String(part.name).toLowerCase()}`}>
                              {part.count} {String(part.name).toLowerCase()}
                            </span>
                          ))}
                        </span>
                      )}
                    </button>
                  );
                })}
          </div>

          {ADD_ACTIONS[selectedResource] && (
            <div className="cp-table-head">
              {selectedResource === "equipments" && (
                <Button
                  className="client-move-equipment"
                  icon="swap-horizontal"
                  text="Move equipment"
                  onClick={this.openMoveEquipmentPanel.bind(this)}
                  disabled={isLoading}
                />
              )}
              <Button
                className="cp-add"
                icon="add"
                text={ADD_ACTIONS[selectedResource].label}
                onClick={() => this.openAdd(ADD_ACTIONS[selectedResource].open)}
              />
            </div>
          )}
          <div className="cp-table" key={`${selectedResource}-${this.state.tableKey}`}>{this.resourceData()}</div>
        </section>

        <AddEstimateModal client={client} isOpen={this.state.openDialog === "estimate"} onClose={this.closeAdd} onSuccess={this.onAdded} />
        <AddInvoiceModal client={client} isOpen={this.state.openDialog === "invoice"} onClose={this.closeAdd} onSuccess={this.onAdded} />
        <AddWorkReportModal client={client} isOpen={this.state.openDialog === "work_order"} onClose={this.closeAdd} onSuccess={this.onAdded} />
        <AddProjectModal client={client} isOpen={this.state.openDialog === "project"} onClose={this.closeAdd} onSuccess={this.onAdded} />
        <AddEquipmentModal client={client} isOpen={this.state.openDialog === "equipment"} onClose={this.closeAdd} onSuccess={this.onAdded} />
        <AddLocationModal client_id={client.id} isOpen={this.state.openDialog === "location"} onClose={this.closeAdd} onSuccess={this.onAdded} />

        <MoveEquipmentPanel
          isOpen={this.state.isMoveEquipmentPanelOpen}
          lockSource={true}
          sourceClientId={attributes.id}
          sourceClientName={attributes.name}
          onClose={this.closeMoveEquipmentPanel.bind(this)}
          onSuccess={this.onMoveEquipmentSuccess.bind(this)}
        />
      </div>
    );
  }
}

// Shown while the client loads (pages/clients/show.js): the page's outline,
// shimmering, instead of a spinner.
const SKELETON_COLUMNS = [
  { area: "a", label: "", width: "minmax(160px, 1.2fr)", bone: "person" },
  { area: "b", label: "", width: "minmax(140px, 1fr)" },
  { area: "c", label: "", width: "minmax(140px, 1fr)", bone: "single" },
  { area: "d", label: "", width: "minmax(120px, 0.8fr)", bone: "single" },
];

export const ClientPageSkeleton = () => (
  <div className="cp cp--loading" role="status" aria-label="Loading client">
    <div className="cp-head" aria-hidden="true">
      <span className="dl-bone cp-avatar" />
      <div className="cp-head__who">
        <span className="dl-bone" style={{ width: 220, height: 20 }} />
        <span className="dl-bone" style={{ width: 300, height: 12, marginTop: 10 }} />
      </div>
      <span className="dl-bone" style={{ width: 120, height: 36, borderRadius: 8 }} />
    </div>
    <div className="cp-tabs" aria-hidden="true">
      {Array.from({ length: 8 }, (_, index) => (
        <span key={index} className="cp-tab cp-tab--loading">
          <span className="dl-bone cp-bone--label" />
          <span className="dl-bone cp-bone--count" />
        </span>
      ))}
    </div>
    <DataList.Loading columns={SKELETON_COLUMNS} minTableWidth={0} noun="records" rows={6} />
  </div>
);

export default ClientProfileCard;
